// piano-frontend/src/pages/UserLessonPage.js - 블록 스케줄 적용

import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Chip, Card, CardContent, Stack, Select, MenuItem, FormControl,
  Tabs, Tab, Tooltip, Alert, AlertTitle
} from "@mui/material";
import { NotificationsActive, Person, Group, AccessTime, Close, NotificationsNone, EventAvailable } from "@mui/icons-material";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import api from "../api/axios";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useDarkMode } from "../App";
import { useDialog } from "../contexts/DialogProvider";
import { enablePush, isPushSupported, getNotificationPermission } from "../utils/push";
import { canInstall, isIOS, promptNativeInstall, installNudgeSnoozed, markInstallNudged } from "../utils/pwa";
import MonthCalendarNav from "../components/MonthCalendarNav";
import { useMonthParam } from "../hooks/useCalendarUrlState";

const LESSON_START_TIMES = [
  "12:00", "12:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30"
];

// 요일별 기본 레슨 시간 — 서버 레슨 설정 로드 전/실패 시의 안전 기본값 (기존 하드코딩과 동일)
const LESSON_DAY_FALLBACK = {
  0: null,
  1: ["14:00", "22:00"],
  2: ["14:00", "22:00"],
  3: ["14:00", "22:00"],
  4: ["14:00", "22:00"],
  5: ["14:00", "18:00"],
  6: ["12:00", "18:00"]
};
const DAY_KEYS_EN = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function getEndTime(start) {
  if (!start) return "";
  let [h, m] = start.split(":").map(Number);
  m += 30;
  h += Math.floor(m / 60);
  m = m % 60;
  if (h > 23) h = 23;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// 대기 항목 날짜의 D-day 배지 정보 (오늘/내일/D-N/지남)
function getWaitlistDday(dateStr) {
  const diff = dayjs(dateStr).startOf("day").diff(dayjs().startOf("day"), "day");
  if (diff < 0) return { label: "지남", tone: "past" };
  if (diff === 0) return { label: "오늘", tone: "today" };
  if (diff === 1) return { label: "내일", tone: "tomorrow" };
  return { label: `D-${diff}`, tone: "upcoming" };
}

// 대기 신청 시점 상대 표시 (created_at 기준)
function getRelativeCreated(created) {
  if (!created) return "";
  const d = dayjs().startOf("day").diff(dayjs(created).startOf("day"), "day");
  if (d <= 0) return "오늘 신청";
  if (d === 1) return "어제 신청";
  return `${d}일 전 신청`;
}

// 선생님 카드 개수에 따른 가로 열 수 (2→2, 3→3, 4→2×2, 5명 이상→3)
function getTeacherColumns(count) {
  if (count <= 1) return 1;
  if (count === 4) return 2;
  return Math.min(count, 3);
}

// 열이 3칸 이상(카드가 좁음)이면 좁은 화면에서 이름이 줄바꿈되지 않도록 폰트를 줄임
function getTeacherNameFontSize(count) {
  return getTeacherColumns(count) >= 3 ? { xs: "1rem", sm: "1.25rem" } : "1.25rem";
}

function UserLessonPage({ user, setUser }) {
  const { darkMode } = useDarkMode();
  const { confirm, alert, toast } = useDialog();
  const [year, month, setYearMonth] = useMonthParam(); // 보던 달은 URL(?month=)에 유지
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [waitlistDialog, setWaitlistDialog] = useState({ open: false });
  const [loading, setLoading] = useState(false);
  const [userTicket, setUserTicket] = useState(user?.ticket ?? 0);
  const [userRescheduleTicketCount, setUserRescheduleTicketCount] = useState(user?.reschedule_ticket_count ?? 0);
  // 레슨 신청 시 변경권 사용 여부 (true: 변경권, false: 일반)
  const [useRescheduleTicket, setUseRescheduleTicket] = useState(false);
  const [existingReservations, setExistingReservations] = useState([]);
  const [timeAvailability, setTimeAvailability] = useState({});
  const [scheduleOverrides, setScheduleOverrides] = useState({});
  // 알림 진입 자동선택 판정 게이트 — 예약현황/변동 스케줄 로드가 끝난 날짜를 기록
  const [reservationsLoadedDate, setReservationsLoadedDate] = useState(null);
  const [overridesLoadedDate, setOverridesLoadedDate] = useState(null);
  const [myWaitlist, setMyWaitlist] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectionMode, setSelectionMode] = useState('time-first');
  const [pendingRequests, setPendingRequests] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [blockSchedules, setBlockSchedules] = useState([]);
  const [lessonWindows, setLessonWindows] = useState(null);
  const [lessonOverrides, setLessonOverrides] = useState([]);
  
  const today = dayjs();
  const location = useLocation();
  const [pendingFocus, setPendingFocus] = useState(null); // 빈자리 알림 진입 시 자동 선택할 시간
  // 알림 재진입 시 같은 날짜여도 예약현황/변동 스케줄을 강제 재조회하기 위한 카운터
  const [focusRefreshNonce, setFocusRefreshNonce] = useState(0);
  // 빈자리 알림 진입 후 스크롤할 단계('teacher' = 선생님 선택, 'time' = 시간 선택)
  const [focusScrollTarget, setFocusScrollTarget] = useState(null);
  const timeStepRef = useRef(null);
  const teacherStepRef = useRef(null);

  // 레슨 설정(요일별 기본 + 예외 날짜) 기반 예약 가능 시작 시간 목록
  function getAllowedTimes(date) {
    const dateStr = dayjs(date).format("YYYY-MM-DD");
    const dow = dayjs(date).day();

    const dayKey = DAY_KEYS_EN[dow];
    const cfgStart = lessonWindows?.[`${dayKey}_start_time`]?.slice(0, 5);
    const cfgEnd = lessonWindows?.[`${dayKey}_end_time`]?.slice(0, 5);
    const fallback = LESSON_DAY_FALLBACK[dow];
    const baseStart = cfgStart || (fallback ? fallback[0] : null);
    const baseEnd = cfgEnd || (fallback ? fallback[1] : null);

    let start = baseStart;
    let end = baseEnd;

    const override = lessonOverrides.find(o => o.date === dateStr);
    if (override) {
      if (override.is_open === false) return [];
      start = override.start_time ? override.start_time.slice(0, 5) : baseStart;
      end = override.end_time ? override.end_time.slice(0, 5) : baseEnd;
    }

    if (!start || !end) return [];

    const slots = [];
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let cur = sh * 60 + sm;
    const endMin = eh * 60 + em;
    while (cur + 30 <= endMin) {
      slots.push(`${String(Math.floor(cur / 60)).padStart(2, "0")}:${String(cur % 60).padStart(2, "0")}`);
      cur += 30;
    }
    return slots;
  }

  // getAllowedTimes + 변동 스케줄 시간을 합친 확장 시간 슬롯
  function getExtendedAllowedTimes(date) {
    const baseTimes = getAllowedTimes(date);
    const overrideTimes = new Set(baseTimes);

    Object.values(scheduleOverrides).forEach(override => {
      if (override.type === 'ADJUSTED' && override.work_start && override.work_end) {
        const start = override.work_start.slice(0, 5);
        const end = override.work_end.slice(0, 5);

        LESSON_START_TIMES.forEach(time => {
          if (time >= start && time < end) {
            overrideTimes.add(time);
          }
        });
      }
    });

    return [...overrideTimes].sort();
  }

  useEffect(() => {
    api.get("/teachers")
      .then(res => setTeachers(res.data))
      .catch(() => setTeachers([]));

    // 레슨 요일별 기본 시간 + 예외 날짜 (실패 시 기본값으로 동작)
    api.get("/lesson-config")
      .then(res => setLessonWindows(res.data))
      .catch(() => setLessonWindows(null));
    api.get("/lesson-config/overrides")
      .then(res => setLessonOverrides(res.data))
      .catch(() => setLessonOverrides([]));

    loadMyWaitlist();
    loadBlockSchedules();
  }, []);

  // 알림함 "예약하기/바로가기"로 진입 시 해당 날짜를 자동 선택.
  // 이미 이 페이지가 열린 상태에서 알림을 눌러도 동작하도록 location 변화마다 재적용한다.
  useEffect(() => {
    const st = location.state || {};
    const focusDate = st.focusDate;
    if (!focusDate) return;
    const d = dayjs(focusDate);
    if (!d.isValid()) return;

    setYearMonth(d.year(), d.month());
    setSelectedDate(focusDate);
    setSelectedTime(null);
    setSelectedTeacher(null);
    setAvailableTeachers([]);
    setSelectionMode('time-first');
    setPendingRequests({});
    setTabValue(0);

    // 빈자리 알림에서 온 경우: 대기했던 시간까지 자동 선택 (데이터 로드 후 검증).
    // 같은 날짜 재진입이어도 최신 데이터로 판정하도록 로드 완료 표시를 지우고 재조회를 강제한다.
    if (st.focusReserve && st.focusTime) {
      setPendingFocus({ date: focusDate, time: String(st.focusTime).slice(0, 5) });
      setReservationsLoadedDate(null);
      setOverridesLoadedDate(null);
      setFocusRefreshNonce(n => n + 1);
    }

    // state 소비 — 새로고침/뒤로가기 시 재적용 방지
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // 빈자리 알림 진입 시: 해당 날짜의 예약현황과 변동 스케줄이 "모두" 로드된 뒤
  // 대기했던 시간을 자동 선택하고, 이미 마감되었으면 안내만 표시한다.
  // timeAvailability는 변동 스케줄 도착 전 값으로 먼저 갱신될 수 있어(기본 근무만으로
  // '마감' 오판하는 레이스) 파생 상태 대신 원본 상태로 직접 판정한다.
  useEffect(() => {
    if (!pendingFocus || selectedDate !== pendingFocus.date) return;
    if (reservationsLoadedDate !== pendingFocus.date || overridesLoadedDate !== pendingFocus.date) return;
    const timeStr = pendingFocus.time;
    setPendingFocus(null);

    const bookedTeacherIds = existingReservations
      .filter(r => r.start_time.slice(0, 5) === timeStr)
      .map(r => r.teacher_id);
    const available = teachers.some(teacher => {
      const schedules = getEffectiveSchedule(teacher, selectedDate);
      if (!schedules) return false;
      if (isBlockedBySchedule(teacher.id, selectedDate, timeStr)) return false;
      if (bookedTeacherIds.includes(teacher.id)) return false;
      return schedules.some(s => timeStr >= s.start.slice(0, 5) && timeStr < s.end.slice(0, 5));
    });

    if (available) {
      setSelectedTime(timeStr);
      setFocusScrollTarget('teacher');
    } else {
      toast('아쉽지만 방금 이 시간이 마감되었어요. 다른 시간을 선택해 주세요.', 'warning');
      setFocusScrollTarget('time');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFocus, selectedDate, reservationsLoadedDate, overridesLoadedDate]);

  // 알림 바로가기로 날짜·시간이 자동 선택되면 바로 예약할 수 있게 해당 단계로 스크롤한다.
  // (마감된 경우에는 다른 시간을 고르도록 시간 선택 단계로 보낸다)
  useEffect(() => {
    if (!focusScrollTarget) return;
    // 선생님 목록이 렌더되어 높이가 잡힌 뒤 이동한다.
    // 상태 해제(setFocusScrollTarget)는 반드시 이동 "후"에 — 먼저 해제하면 의존성이 바뀌며
    // 이 effect의 cleanup이 돌아 타이머가 취소되어 스크롤이 아예 실행되지 않는다.
    const t = setTimeout(() => {
      const el = focusScrollTarget === 'teacher' ? teacherStepRef.current : timeStepRef.current;
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setFocusScrollTarget(null);
    }, 150);
    return () => clearTimeout(t);
  }, [focusScrollTarget]);

  // 블록 스케줄 로드
  const loadBlockSchedules = async () => {
    try {
      const res = await api.get("/block-schedules");
      setBlockSchedules(res.data);
    } catch (err) {
      console.error("블록 스케줄 로드 실패:", err);
      setBlockSchedules([]);
    }
  };

  // 블록 스케줄 확인 함수
  const isBlockedBySchedule = (teacherId, date, time) => {
    const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const dayOfWeek = dayNames[dayjs(date).day()];
    
    const blockSchedule = blockSchedules.find(bs => 
      bs.teacher_id === teacherId && 
      bs.day_of_week === dayOfWeek && 
      bs.start_time.slice(0, 5) === time
    );

    if (!blockSchedule) return false;

    // 예외 확인
    const override = blockSchedule.overrides?.find(o => o.date === date);
    
    // 예외가 있고 is_open이 true면 블록 해제
    if (override && override.is_open) {
      return false;
    }

    return true;
  };

  // 휴일 목록 조회
  useEffect(() => {
    const startDate = dayjs(`${year}-${month + 1}-01`).subtract(1, 'month').format('YYYY-MM-DD');
    const endDate = dayjs(`${year}-${month + 1}-01`).add(2, 'month').format('YYYY-MM-DD');
    
    api.get("/holidays", { params: { start_date: startDate, end_date: endDate } })
      .then(res => setHolidays(res.data))
      .catch(() => setHolidays([]));
  }, [year, month]);

  const isHoliday = (dateStr) => {
    return holidays.some(h => h.date === dateStr);
  };

  const getHolidayReason = (dateStr) => {
    const holiday = holidays.find(h => h.date === dateStr);
    return holiday?.reason || '휴일';
  };

  const loadMyWaitlist = async () => {
    try {
      const res = await api.get("/lesson-waitlist/my");
      setMyWaitlist(res.data);
    } catch (err) {
      console.error("대기 알림 목록 조회 실패", err);
    }
  };

  // Pull-to-Refresh 핸들러
  const handleRefresh = useCallback(async () => {
    // 기본 데이터 새로고침
    const promises = [
      api.get("/teachers").then(res => setTeachers(res.data)).catch(() => setTeachers([])),
      loadMyWaitlist(),
      loadBlockSchedules(),
    ];

    // 휴일 다시 로드
    const startDate = dayjs(`${year}-${month + 1}-01`).subtract(1, 'month').format('YYYY-MM-DD');
    const endDate = dayjs(`${year}-${month + 1}-01`).add(2, 'month').format('YYYY-MM-DD');
    promises.push(
      api.get("/holidays", { params: { start_date: startDate, end_date: endDate } })
        .then(res => setHolidays(res.data))
        .catch(() => setHolidays([]))
    );

    await Promise.all(promises);

    // 선택된 날짜가 있으면 해당 날짜 데이터도 새로고침
    if (selectedDate) {
      try {
        const res = await api.get("/lessons", { params: { date: selectedDate } });
        setExistingReservations(res.data);
      } catch {
        setExistingReservations([]);
      }

      try {
        const res = await api.get("/lesson-requests/pending", { params: { date: selectedDate } });
        setPendingRequests(res.data);
      } catch {
        setPendingRequests({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, selectedDate]);

  // 백그라운드에 있던 탭이 다시 보이면 데이터를 새로 불러온다.
  // 웹 푸시 클릭 시 SW(custom-sw.js)는 열려 있는 탭을 focus만 하고 새로고침하지 않으므로,
  // 이 갱신이 없으면 알림을 눌러 들어와도 변동 전 데이터가 그대로 보인다.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') handleRefresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [handleRefresh]);

  // 조회는 "날짜가 바뀌거나 강제 새로고침일 때"만 한다.
  // (선생님/블록/운영시간 등은 예약현황 응답에 영향을 주지 않으므로 재조회 대상이 아니다 —
  //  이들이 도착할 때마다 재조회하면 응답이 여러 번 도착하며 화면이 깜빡인다)
  useEffect(() => {
    if (!selectedDate) return;
    api.get("/lessons", { params: { date: selectedDate } })
      .then(res => {
        setExistingReservations(res.data);
        setReservationsLoadedDate(selectedDate);
      })
      .catch(() => {
        setExistingReservations([]);
        setReservationsLoadedDate(selectedDate);
      });

    api.get("/lesson-requests/pending", { params: { date: selectedDate } })
      .then(res => setPendingRequests(res.data))
      .catch(() => setPendingRequests({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, focusRefreshNonce]);

  // 가용성 계산은 계산에 쓰이는 값이 바뀔 때만 (조회와 분리)
  useEffect(() => {
    if (!selectedDate) return;
    calculateTimeAvailability(existingReservations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, existingReservations, scheduleOverrides, teachers, user, blockSchedules, lessonWindows, lessonOverrides]);

  useEffect(() => {
    if (!selectedDate || teachers.length === 0) return;

    async function fetchOverrides() {
      const results = {};
      await Promise.all(
        teachers.map(async (t) => {
          try {
            const res = await api.get(`/teachers/${t.id}/schedule-overrides`);
            const matchingOverride = res.data.find(o => o.date === selectedDate);
            if (matchingOverride) {
              results[t.id] = matchingOverride;
            }
          } catch (e) {
            console.error("override fetch failed:", e);
          }
        })
      );
      setScheduleOverrides(results);
      setOverridesLoadedDate(selectedDate);
    }

    fetchOverrides();
  }, [selectedDate, teachers, focusRefreshNonce]);

  const getPendingCount = (time, teacherId) => {
    if (!time) return 0;
    if (!teacherId) {
      let total = 0;
      Object.keys(pendingRequests).forEach(key => {
        if (key.startsWith(`${time.slice(0,5)}_`)) {
          total += pendingRequests[key];
        }
      });
      return total;
    }
    return pendingRequests[`${time.slice(0,5)}_${teacherId}`] || 0;
  };

  // 이 날짜/시간에 내가 걸어둔 대기 알림 (없으면 null). teacherId 지정 시 해당 선생님 또는 전체(선생님 미지정) 대기 포함
  const getMyWaitlist = (time, teacherId) => {
    if (!time || !selectedDate) return null;
    const t = time.slice(0, 5);
    return myWaitlist.find(w => {
      const wTeacherId = w.teacher_id ?? w.teacher?.id ?? null;
      return dayjs(w.date).format("YYYY-MM-DD") === dayjs(selectedDate).format("YYYY-MM-DD") &&
        w.start_time.slice(0, 5) === t &&
        (teacherId == null || wTeacherId == null || wTeacherId === teacherId);
    }) || null;
  };

  const calculateTimeAvailability = (reservations) => {
    if (!selectedDate || teachers.length === 0) {
      setTimeAvailability({});
      return;
    }

    const availability = {};

    LESSON_START_TIMES.forEach(time => {
      const timeStr = time.slice(0, 5);
      
      const workingTeachers = teachers.filter((teacher) => {
        const schedules = getEffectiveSchedule(teacher, selectedDate);
        if (!schedules) return false;

        // 블록 스케줄 확인
        if (isBlockedBySchedule(teacher.id, selectedDate, timeStr)) {
          return false;
        }

        return schedules.some(schedule => {
          return timeStr >= schedule.start.slice(0,5) &&
                timeStr < schedule.end.slice(0,5);
        });
      });

      const bookedTeachers = reservations.filter(r => 
        r.start_time.slice(0, 5) === timeStr
      ).map(r => r.teacher_id);

      const availableTeachersCount = workingTeachers.filter(
        t => !bookedTeachers.includes(t.id)
      ).length;

      const isMyReservation = reservations.some(r =>
        r.start_time.slice(0, 5) === timeStr && r.user_id === user?.id
      );

      availability[time] = {
        available: availableTeachersCount > 0,
        workingCount: workingTeachers.length,
        bookedCount: bookedTeachers.length,
        availableCount: availableTeachersCount,
        isMyReservation
      };
    });

    setTimeAvailability(availability);
  };

  useEffect(() => {
    if (selectedDate && selectedTime && selectionMode === 'time-first') {
      const timeStr = selectedTime.slice(0, 5);

      const workingTeachers = teachers.filter(teacher => {
        const schedules = getEffectiveSchedule(teacher, selectedDate);
        if (!schedules) return false;
        
        // 블록 스케줄 확인
        if (isBlockedBySchedule(teacher.id, selectedDate, timeStr)) {
          return false;
        }
        
        return schedules.some(schedule => {
          return timeStr >= schedule.start.slice(0, 5) && timeStr < schedule.end.slice(0, 5);
        });
      });

      const bookedTeacherIds = existingReservations
        .filter(r => r.start_time.slice(0, 5) === timeStr)
        .map(r => r.teacher_id);

      const available = workingTeachers.filter(
        t => !bookedTeacherIds.includes(t.id)
      );

      setAvailableTeachers(available);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedTime, teachers, existingReservations, selectionMode, blockSchedules, lessonWindows, lessonOverrides]);

  const firstDay = dayjs(`${year}-${month + 1}-01`);
  const firstDow = firstDay.day();
  const lastDate = firstDay.endOf("month").date();
  const prevMonthLastDate = firstDay.subtract(1, "month").endOf("month").date();
  const calendarDays = [];

  for (let i = firstDow - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonthLastDate - i,
      isCurrentMonth: false,
      month: month === 0 ? 11 : month - 1,
      year: month === 0 ? year - 1 : year
    });
  }

  for (let day = 1; day <= lastDate; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: true,
      month,
      year
    });
  }

  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: false,
      month: month === 11 ? 0 : month + 1,
      year: month === 11 ? year + 1 : year
    });
  }

  function getEffectiveSchedule(teacher, date) {
    const override = scheduleOverrides[teacher.id];
    const dow = ["SUN","MON","TUE","WED","THU","FRI","SAT"][dayjs(date).day()];

    if (override) {
      if (override.type === "OFF") {
        return null;
      }
      if (override.type === "ADJUSTED") {
        return [{
          start: override.work_start,
          end: override.work_end
        }];
      }
    }

    return teacher.schedules.filter(s => s.day === dow);
  }

  function handleDateClick(dateInfo) {
    const { day, month: m, year: y } = dateInfo;
    const clickedDate = dayjs(`${y}-${m + 1}-${day}`);
    const dow = clickedDate.day();
    const dateStr = clickedDate.format("YYYY-MM-DD");

    // 휴일 체크
    if (isHoliday(dateStr)) {
      return;
    }

    const twoMonthsLater = today.add(2, 'month');
    if (clickedDate.isAfter(twoMonthsLater)) {
      toast('예약 가능한 기간이 아닙니다.\n현재 날짜 기준 2개월 이내만 예약 가능합니다.', 'warning');
      return;
    }

    if (dow !== 0 && clickedDate.isAfter(today.subtract(1, "day"))) {
      setSelectedDate(dateStr);
      setSelectedTime(null);
      setSelectedTeacher(null);
      setAvailableTeachers([]);
      setSelectionMode('time-first');
      setPendingRequests({});
    }
  }

  function openConfirmDialog(teacher, date, time) {
    const regularCount = userTicket - userRescheduleTicketCount;
    // 일반 티켓이 없고 변경권만 있는 경우 변경권 자동 선택
    setUseRescheduleTicket(regularCount <= 0 && userRescheduleTicketCount > 0);
    setConfirmDialog({ open: true, teacher, date, time });
  }

  async function handleTeacherSelect(teacher) {
    if (selectionMode === 'time-first') {
      openConfirmDialog(teacher, selectedDate, selectedTime);
    } else {
      setSelectedTeacher(teacher);
      setSelectedTime(null);
    }
  }

  async function handleTimeSelect(time) {
    if (selectionMode === 'teacher-first') {
      const timeStr = time.slice(0, 5);
      
      const myReservation = existingReservations.find(r => 
        r.teacher_id === selectedTeacher.id && 
        r.start_time.slice(0, 5) === timeStr && 
        r.user_id === user?.id
      );

      if (myReservation) {
        return;
      }

      const isBooked = existingReservations.some(r => 
        r.teacher_id === selectedTeacher.id && r.start_time.slice(0, 5) === timeStr
      );

      if (isBooked) {
        setWaitlistDialog({
          open: true,
          date: selectedDate,
          time: time,
          teacher_id: selectedTeacher.id,
          teacher_name: selectedTeacher.name
        });
        return;
      }

      openConfirmDialog(selectedTeacher, selectedDate, time);
    } else {
      const availability = timeAvailability[time];
      
      if (availability?.isMyReservation) {
        return;
      }
      
      if (!availability?.available) {
        setWaitlistDialog({
          open: true,
          date: selectedDate,
          time: time
        });
        return;
      }

      setSelectedTime(time);
    }
  }

  async function handleConfirm() {
    if (!confirmDialog.teacher || !selectedDate || !confirmDialog.time) return;

    try {
      setLoading(true);
      const response = await api.post("/lesson-requests", {
        teacher_id: confirmDialog.teacher.id,
        date: selectedDate,
        start_time: confirmDialog.time,
        end_time: getEndTime(confirmDialog.time),
        use_reschedule_ticket: useRescheduleTicket,
      });

      if (response.data.remainingTickets !== undefined) {
        setUserTicket(response.data.remainingTickets);
        if (setUser) {
          setUser(prev => ({
            ...prev,
            ticket: response.data.remainingTickets,
            reschedule_ticket_count: response.data.rescheduleTicketCount ?? prev.reschedule_ticket_count,
          }));
        }
        setUserRescheduleTicketCount(response.data.rescheduleTicketCount ?? userRescheduleTicketCount);
      }

      const msg = useRescheduleTicket
        ? "레슨 신청이 완료되었습니다.\n변경권 티켓 1개가 차감되었으며, 관리자 승인 후 예약이 확정됩니다.\n\n⚠️ 이 레슨은 일정 변경으로 예약된 레슨으로, 추가 취소 신청이 불가합니다."
        : "레슨 신청이 완료되었습니다.\n티켓 1개가 차감되었으며, 관리자 승인 후 예약이 확정됩니다.";
      toast(msg);
      setConfirmDialog({ open: false });
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedTeacher(null);
      setAvailableTeachers([]);
    } catch (err) {
      toast(err?.response?.data?.message || "신청 실패", "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleWaitlistConfirm() {
    if (!waitlistDialog.date || !waitlistDialog.time) return;

    let registered = false;
    try {
      setLoading(true);
      await api.post("/lesson-waitlist", {
        date: waitlistDialog.date,
        start_time: waitlistDialog.time,
        teacher_id: waitlistDialog.teacher_id || null
      });

      const message = waitlistDialog.teacher_name
        ? `대기 알림이 신청되었습니다.\n${waitlistDialog.teacher_name} 선생님의 ${waitlistDialog.date} ${waitlistDialog.time} 시간에 예약이 가능해지면 알림을 보내드립니다.`
        : `대기 알림이 신청되었습니다.\n${waitlistDialog.date} ${waitlistDialog.time} 시간에 예약이 가능해지면 알림을 보내드립니다.`;

      toast(message);
      setWaitlistDialog({ open: false });
      loadMyWaitlist();
      registered = true;
    } catch (err) {
      toast(err?.response?.data?.message || "대기 알림 신청 실패", "error");
    } finally {
      setLoading(false);
    }

    if (!registered) return;

    // 실시간 알림 받으려면: (선행) 홈 화면 설치 → (그 다음) 푸시 권한.
    // A. 미설치 & 설치 유도 가능 → 홈 화면 추가 유도 (푸시 수신의 선행조건)
    if (canInstall() && !installNudgeSnoozed()) {
      markInstallNudged();
      if (isIOS()) {
        // iOS는 수동 설치만 가능 → 공유 → 홈 화면에 추가 안내
        await alert({
          title: '홈 화면에 추가하면 알림을 받아요',
          message: '실시간 빈자리 알림은 홈 화면에 추가한 앱에서만 받을 수 있어요.\n\n1) 하단 공유 버튼 탭\n2) "홈 화면에 추가" 선택',
          confirmText: '알겠어요',
        });
      } else {
        // 안드로이드/데스크톱 → 네이티브 설치 프롬프트 (원탭)
        const ok = await confirm({
          title: '앱으로 설치할까요?',
          message: '홈 화면에 추가하면 빈자리가 생기는 즉시 알림을 받을 수 있어요.',
          confirmText: '설치',
          cancelText: '나중에',
        });
        if (ok) {
          const outcome = await promptNativeInstall();
          if (outcome === 'accepted') {
            toast('설치되었어요. 설치한 앱을 열면 알림을 켤 수 있어요.');
          }
        }
      }
      return;
    }

    // B. 설치됨(또는 브라우저 푸시 지원) & 미허용 → 푸시 켜기 유도
    // (인앱 알림함은 앱을 열어야 보이는 폴백이라, 즉시성 있는 푸시를 권장)
    if (isPushSupported() && getNotificationPermission() !== 'granted') {
      const ok = await confirm({
        title: '실시간 알림 받기',
        message: '푸시 알림을 켜면 빈자리가 생기는 즉시 바로 알려드려요.\n지금 켜시겠어요?',
        confirmText: '알림 켜기',
        cancelText: '나중에',
      });
      if (ok) {
        try {
          await enablePush();
          toast('알림이 켜졌어요. 빈자리가 나면 바로 알려드릴게요.');
        } catch (e) {
          if (e.code === 'denied') {
            toast('브라우저 설정에서 알림 권한을 허용해 주세요.', 'warning');
          } else if (e.code !== 'unsupported') {
            toast('알림 켜기에 실패했어요. 설정에서 다시 시도해 주세요.', 'error');
          }
        }
      }
    }
  }

  async function handleCancelWaitlist(id) {
    if (!(await confirm({ title: "대기 알림 취소", message: "대기 알림을 취소하시겠습니까?", confirmText: "취소하기", color: "error" }))) return;

    try {
      await api.delete(`/lesson-waitlist/${id}`);
      toast("대기 알림이 취소되었습니다.");
      loadMyWaitlist();
    } catch (err) {
      toast("대기 알림 취소 실패", "error");
    }
  }

  // 달 이동 — 월/연 선택이나 "오늘"로 점프해 선택한 날짜가 화면 밖으로 나가면
  // 아래 시간/선생님 선택 영역이 보이지 않는 날짜를 붙들고 있지 않도록 선택을 비운다.
  // (◀▶/스와이프 한 칸 이동은 기존처럼 선택 유지)
  function handleMonthChange(y, m, { jump }) {
    setYearMonth(y, m);
    if (jump && selectedDate && !dayjs(selectedDate).isSame(dayjs(new Date(y, m, 1)), "month")) {
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedTeacher(null);
      setAvailableTeachers([]);
    }
  }

  // 예약현황과 변동 스케줄이 "둘 다" 이 날짜 기준으로 도착해야 가용 여부가 확정된다.
  // 도착 전에는 예약 가능/불가를 표시하지 않는다(부분 데이터로 판정 → 값이 뒤집히며 깜빡임).
  const slotsReady =
    teachers.length > 0 &&
    reservationsLoadedDate === selectedDate &&
    overridesLoadedDate === selectedDate;

  // teacher-first 모드: 선택한 날짜에 근무 스케줄(예외 반영)이 있는 선생님만 노출
  const workingTeachersForDate = selectedDate
    ? teachers.filter(t => {
        const schedules = getEffectiveSchedule(t, selectedDate);
        return schedules && schedules.length > 0;
      })
    : [];

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent={
        <Box sx={{ textAlign: "center", py: 2, color: darkMode ? "#aaa" : "#666" }}>
          ↓ 당겨서 새로고침
        </Box>
      }
      refreshingContent={
        <Box sx={{ textAlign: "center", py: 2, color: "primary.main" }}>
          새로고침 중...
        </Box>
      }
    >
    <Box sx={{ pt: { xs: 0, sm: 2 }, pb: 2, maxWidth: 900, mx: "auto", px: { xs: 1, sm: 2 } }}>
      {/* 모바일은 헤더에 페이지 제목이 표시되므로 본문 제목 숨김 */}
      <Typography variant="h5" align="center" fontWeight="bold" sx={{ mb: 3, display: { xs: "none", sm: "block" } }}>
        레슨 신청
      </Typography>

      {/* 모바일: 탭을 화면 폭 전체로 헤더 바로 아래에 붙여 헤더와 한 덩어리로 보이게 한다 */}
      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        centered
        sx={{
          mb: 2,
          mx: { xs: -1, sm: 0 },
          bgcolor: { xs: "background.paper", sm: "transparent" },
          borderBottom: { xs: 1, sm: 0 },
          borderColor: "divider",
          "& .MuiTab-root": { flex: { xs: 1, sm: "0 0 auto" }, maxWidth: { xs: "none", sm: 360 } },
        }}
      >
        <Tab label="레슨 신청" />
        <Tab label={`레슨 대기 (${myWaitlist.length})`} />
      </Tabs>

      {tabValue === 0 ? (
        <>
          <Box sx={{ mb: 2, textAlign: "right" }}>
            <Typography variant="body2" color="text.secondary">
              남은 레슨 티켓:{" "}
              <strong style={{ color: "#1976d2", fontSize: "1.0rem" }}>
                일반 티켓 {userTicket - userRescheduleTicketCount}개
              </strong>
              {userRescheduleTicketCount > 0 && (
                <span style={{ color: "#ed6c02", fontSize: "1.0rem", marginLeft: 6 }}>
                  / 변경 티켓 <strong>{userRescheduleTicketCount}개</strong>
                </span>
              )}
            </Typography>
          </Box>

          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 4,
              overflow: 'hidden',
            }}
          >
            {/* 예약 가능 범위(이번 달 ~ 2개월 후) 밖의 달은 월 선택에서 흐리게 */}
            <MonthCalendarNav
              year={year}
              month={month}
              onChange={handleMonthChange}
              minYm={{ year: today.year(), month: today.month() }}
              maxYm={{ year: today.add(2, "month").year(), month: today.add(2, "month").month() }}
            >
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, mb: 1 }}>
                {["일", "월", "화", "수", "목", "금", "토"].map((d, i) => (
                  <Box key={i} sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    color: i === 0 ? "#d32f2f" : "inherit",
                    fontSize: "0.9rem"
                  }}>{d}</Box>
                ))}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
            {calendarDays.map((dateInfo, idx) => {
              const { day, isCurrentMonth, month: m, year: y } = dateInfo;
              const dateObj = dayjs(`${y}-${m + 1}-${day}`);
              const dow = dateObj.day();
              const isToday = dateObj.isSame(today, "day");
              const isPast = dateObj.isBefore(today, "day");
              const isSunday = dow === 0;
              const dateStr = dateObj.format("YYYY-MM-DD");
              
              const twoMonthsLater = today.add(2, 'month');
              const isBeyondTwoMonths = dateObj.isAfter(twoMonthsLater);
              
              const holidayDate = isHoliday(dateStr);
              const holidayReason = holidayDate ? getHolidayReason(dateStr) : null;
              
              const isClickable = !isSunday && isCurrentMonth && !isPast && !isBeyondTwoMonths && !holidayDate;
              const isSelected = selectedDate === dateStr;

              const cellContent = (
                <Box
                  key={idx}
                  onClick={() => isClickable && handleDateClick(dateInfo)}
                  sx={{
                    aspectRatio: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isClickable ? "pointer" : "default",
                    borderRadius: 2,
                    bgcolor: isSelected
                      ? (darkMode ? "#1565c0" : "#1976d2")
                      : holidayDate
                        ? (darkMode ? "rgba(211, 47, 47, 0.14)" : "#ffebee")
                        : isToday
                          ? (darkMode ? "rgba(255, 152, 0, 0.10)" : "#fff8e1")
                          : isClickable
                            ? (darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.035)")
                            : "transparent",
                    color: isSelected
                      ? "#fff"
                      : holidayDate
                        ? (darkMode ? "#e57373" : "#d32f2f")
                        : !isCurrentMonth
                          ? (darkMode ? "#666" : "#ccc")
                          : "inherit",
                    fontWeight: isToday || isSelected || holidayDate ? "bold" : "normal",
                    opacity: (isPast || isSunday || isBeyondTwoMonths) && !isToday ? 0.4 : 1,
                    border: isToday ? "1.5px solid #ff9800" : "none",
                    fontSize: "0.9rem",
                    transition: "all 0.2s",
                    position: "relative",
                    // 선택된 칸은 hover 배경에서 제외 — 덮어쓰면 파란 채움이 하늘색으로 바뀌어
                    // 흰 숫자가 묻힌다 (클릭 후 포인터가 그대로 남아 hover가 유지됨)
                    "&:hover": isClickable && !isSelected ? {
                      backgroundColor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd",
                      transform: "scale(1.05)"
                    } : {}
                  }}
                >
                  {day}
                </Box>
              );

              return holidayDate && isCurrentMonth ? (
                <Tooltip key={idx} title={holidayReason} arrow placement="top">
                  {cellContent}
                </Tooltip>
              ) : cellContent;
            })}
              </Box>
            </MonthCalendarNav>

              {/* 휴일 범례 */}
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, justifyContent: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: darkMode ? "rgba(211, 47, 47, 0.14)" : "#ffebee", border: "1px solid #d32f2f", borderRadius: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">휴일</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: darkMode ? "rgba(255, 152, 0, 0.10)" : "#fff8e1", border: "1.5px solid #ff9800", borderRadius: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">오늘</Typography>
                </Box>
              </Box>
          </Paper>

          {selectedDate && (
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectionMode}
                  onChange={(e) => {
                    setSelectionMode(e.target.value);
                    setSelectedTime(null);
                    setSelectedTeacher(null);
                    setAvailableTeachers([]);
                  }}
                  sx={{ fontWeight: 600 }}
                >
                  <MenuItem value="time-first">시간을 선택하고 선생님을 선택하기</MenuItem>
                  <MenuItem value="teacher-first">선생님을 선택하고 시간을 선택하기</MenuItem>
                </Select>
              </FormControl>
            </Paper>
          )}

          {selectedDate && selectionMode === 'time-first' && (
            <Paper ref={timeStepRef} sx={{ p: { xs: 2, sm: 3 }, mb: 4, scrollMarginTop: "64px" }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: 700, mb: 0.5, fontSize: "0.85rem" }}>
                  STEP 1
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  시간을 선택하세요
                </Typography>
                <Alert
                  severity="info"
                  icon={<NotificationsActive fontSize="inherit" />}
                  sx={{ mt: 1, "& .MuiAlert-message": { py: 0.5 } }}
                >
                  <AlertTitle sx={{ fontWeight: 700, fontSize: "0.85rem", mb: 0.25 }}>
                    예약 불가 상태여도 괜찮아요
                  </AlertTitle>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}>
                    시간을 눌러 <strong>대기 알림</strong>을 신청하면, 자리가 나는 즉시 알려드려요.
                  </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedDate} ({["일", "월", "화", "수", "목", "금", "토"][dayjs(selectedDate).day()]}요일)
                </Typography>
              </Box>

              <Box
                sx={{
                  maxWidth: "800px",
                  mx: "auto",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                  gap: 1.5,
                }}
              >
                {getExtendedAllowedTimes(selectedDate).map(time => {
                  const isSelected = selectedTime === time;
                  const availability = timeAvailability[time];
                  const isAvailable = availability?.available;

                  const timeStr = time.slice(0, 5);
                  const myReservation = existingReservations.find(r =>
                    r.start_time.slice(0, 5) === timeStr && r.user_id === user?.id
                  );
                  const myWaitlistEntry = getMyWaitlist(time);
                  const isWaitlisted = !myReservation && !!myWaitlistEntry;

                  return (
                    <Box
                      key={time}
                      onClick={() => {
                        if (myReservation || !slotsReady) return;
                        if (isWaitlisted) { handleCancelWaitlist(myWaitlistEntry.id); return; }
                        handleTimeSelect(time);
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        cursor: myReservation || !slotsReady ? "default" : "pointer",
                        bgcolor: isSelected ? (darkMode ? "#1565c0" : "#1976d2") : myReservation ? (darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd") : isWaitlisted ? (darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd") : (darkMode ? "grey.900" : "#fff"),
                        color: isSelected ? "#fff" : myReservation ? "#1976d2" : isWaitlisted ? "#1976d2" : (darkMode ? "#e0e0e0" : "#333"),
                        border: isSelected ? "2px solid #1565c0" : myReservation ? "2px solid #1976d2" : isWaitlisted ? "2px dashed #1976d2" : isAvailable ? (darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0") : (darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #ddd"),
                        textAlign: "center",
                        transition: "all 0.2s",
                        minHeight: "85px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        "&:hover": !myReservation ? {
                          borderColor: "#1976d2",
                          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)"
                        } : {}
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {time}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 0.5 }}>
                        ~{getEndTime(time)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: isSelected ? "#fff" : myReservation ? "#1976d2" : isWaitlisted ? "#1976d2" : isAvailable ? "#4caf50" : (darkMode ? "#8a8a8a" : "#999"),
                          fontSize: "0.7rem"
                        }}
                      >
                        {!slotsReady ? "확인 중" : myReservation ? "나의 레슨" : isWaitlisted ? <><NotificationsActive sx={{ fontSize: "0.8rem", verticalAlign: "-2px", mr: 0.25 }} />대기중</> : isAvailable ? "예약 가능" : "예약 불가"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          )}

          {selectedDate && selectionMode === 'teacher-first' && (
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: 700, mb: 0.5, fontSize: "0.85rem" }}>
                  STEP 1
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  선생님을 선택하세요
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedDate} ({["일", "월", "화", "수", "목", "금", "토"][dayjs(selectedDate).day()]}요일)
                </Typography>
              </Box>

              {teachers.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    선생님 정보를 불러올 수 없습니다.
                  </Typography>
                </Box>
              ) : workingTeachersForDate.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    이 날짜에 근무하는 선생님이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    maxWidth: "900px",
                    mx: "auto",
                    display: "grid",
                    gridTemplateColumns: `repeat(${getTeacherColumns(workingTeachersForDate.length)}, 1fr)`,
                    gap: 2,
                  }}
                >
                  {workingTeachersForDate.map(teacher => {
                    const isSelected = selectedTeacher?.id === teacher.id;
                    return (
                      <Card key={teacher.id} sx={{
                        cursor: "pointer",
                        height: "100%",
                        transition: "all 0.3s",
                        border: isSelected ? "2px solid #1976d2" : (darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0"),
                        bgcolor: isSelected ? (darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd") : (darkMode ? "grey.900" : "#fff"),
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: 6
                        }
                      }}>
                        <CardContent
                          onClick={() => handleTeacherSelect(teacher)}
                          sx={{ textAlign: "center", p: { xs: 1.25, sm: 2.5 }, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                        >
                          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, fontSize: getTeacherNameFontSize(workingTeachersForDate.length), color: isSelected ? "#1976d2" : "inherit" }}>
                            {teacher.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            선생님
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Paper>
          )}

          {selectedDate && selectedTeacher && selectionMode === 'teacher-first' && (
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: 700, mb: 0.5, fontSize: "0.85rem" }}>
                  STEP 2
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  시간을 선택하세요
                </Typography>
                <Alert
                  severity="info"
                  icon={<NotificationsActive fontSize="inherit" />}
                  sx={{ mt: 1, "& .MuiAlert-message": { py: 0.5 } }}
                >
                  <AlertTitle sx={{ fontWeight: 700, fontSize: "0.85rem", mb: 0.25 }}>
                    예약 불가 상태여도 괜찮아요
                  </AlertTitle>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}>
                    시간을 눌러 <strong>대기 알림</strong>을 신청하면, 자리가 나는 즉시 알려드려요.
                  </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedTeacher.name} 선생님 | {selectedDate} ({["일", "월", "화", "수", "목", "금", "토"][dayjs(selectedDate).day()]}요일)
                </Typography>
              </Box>

              <Box
                sx={{
                  maxWidth: "800px",
                  mx: "auto",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
                  gap: 1.5,
                }}
              >
                {getExtendedAllowedTimes(selectedDate).map(time => {
                  const timeStr = time.slice(0, 5);

                  // 블록 스케줄 확인
                  if (isBlockedBySchedule(selectedTeacher.id, selectedDate, timeStr)) {
                    return null;
                  }

                  const schedules = getEffectiveSchedule(selectedTeacher, selectedDate);
                  const isInWorkingHours = schedules && schedules.some(schedule =>
                    timeStr >= schedule.start.slice(0, 5) && timeStr < schedule.end.slice(0, 5)
                  );

                  if (!isInWorkingHours) return null;

                  const isBooked = existingReservations.some(r =>
                    r.teacher_id === selectedTeacher.id && r.start_time.slice(0, 5) === timeStr
                  );

                  const myReservation = existingReservations.find(r =>
                    r.teacher_id === selectedTeacher.id &&
                    r.start_time.slice(0, 5) === timeStr &&
                    r.user_id === user?.id
                  );

                  const isAvailable = !isBooked;
                  const pendingCount = getPendingCount(time, selectedTeacher.id);
                  const myWaitlistEntry = getMyWaitlist(time, selectedTeacher.id);
                  const isWaitlisted = !myReservation && !!myWaitlistEntry;

                  return (
                    <Box
                      key={time}
                      onClick={() => {
                        if (myReservation || !slotsReady) return;
                        if (isWaitlisted) { handleCancelWaitlist(myWaitlistEntry.id); return; }
                        handleTimeSelect(time);
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        cursor: myReservation || !slotsReady ? "default" : "pointer",
                        bgcolor: myReservation ? (darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd") : isWaitlisted ? (darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd") : (darkMode ? "grey.900" : "#fff"),
                        color: myReservation ? "#1976d2" : isWaitlisted ? "#1976d2" : (darkMode ? "#e0e0e0" : "#333"),
                        border: myReservation ? "2px solid #1976d2" : isWaitlisted ? "2px dashed #1976d2" : isAvailable ? (darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0") : (darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #ddd"),
                        textAlign: "center",
                        transition: "all 0.2s",
                        minHeight: "85px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        position: "relative",
                        "&:hover": !myReservation ? {
                          borderColor: "#1976d2",
                          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)"
                        } : {}
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {time}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 0.5 }}>
                        ~{getEndTime(time)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: myReservation ? "#1976d2" : isWaitlisted ? "#1976d2" : isAvailable ? "#4caf50" : (darkMode ? "#8a8a8a" : "#999"),
                          fontSize: "0.7rem"
                        }}
                      >
                        {!slotsReady ? "확인 중" : myReservation ? "나의 레슨" : isWaitlisted ? <><NotificationsActive sx={{ fontSize: "0.8rem", verticalAlign: "-2px", mr: 0.25 }} />대기중</> : isAvailable ? "예약 가능" : "예약 불가"}
                      </Typography>
                      {pendingCount > 0 && !myReservation && (
                        <Chip
                          label={`${pendingCount}명 대기`}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            height: 18,
                            fontSize: "0.65rem",
                            bgcolor: "#ff9800",
                            color: "#fff",
                            "& .MuiChip-label": { px: 0.8 }
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          )}

          {selectedDate && selectedTime && selectionMode === 'time-first' && (
            <Paper ref={teacherStepRef} sx={{ p: { xs: 2, sm: 3 }, scrollMarginTop: "64px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: 700, mb: 0.5, fontSize: "0.85rem" }}>
                    STEP 2
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    선생님을 선택하세요
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedDate} ({["일", "월", "화", "수", "목", "금", "토"][dayjs(selectedDate).day()]}요일) | {selectedTime}~{getEndTime(selectedTime)}
                  </Typography>
                </Box>
              </Box>

              {availableTeachers.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    이 시간에 예약 가능한 선생님이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    maxWidth: "900px",
                    mx: "auto",
                    display: "grid",
                    gridTemplateColumns: `repeat(${getTeacherColumns(availableTeachers.length)}, 1fr)`,
                    gap: 2,
                  }}
                >
                  {availableTeachers.map(teacher => {
                    const pendingCount = getPendingCount(selectedTime, teacher.id);
                    return (
                      <Card key={teacher.id} sx={{
                        cursor: "pointer",
                        height: "100%",
                        transition: "all 0.3s",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: 6
                        }
                      }}>
                        <CardContent
                          onClick={() => handleTeacherSelect(teacher)}
                          sx={{ textAlign: "center", p: { xs: 1.25, sm: 2.5 }, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                        >
                          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, fontSize: getTeacherNameFontSize(availableTeachers.length) }}>
                            {teacher.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            선생님
                          </Typography>
                          {pendingCount > 0 && (
                            <Chip
                              label={`${pendingCount}명 대기`}
                              size="small"
                              sx={{ mt: 1.5, bgcolor: "#ff9800", color: "#fff", fontWeight: 600, fontSize: "0.7rem", height: 20, "& .MuiChip-label": { px: 0.75 } }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Paper>
          )}
        </>
      ) : (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              내 레슨 대기
            </Typography>
            {myWaitlist.length > 0 && (
              <Chip
                label={`${myWaitlist.length}건`}
                size="small"
                sx={{ bgcolor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd", color: darkMode ? "#90caf9" : "#1976d2", fontWeight: 700 }}
              />
            )}
          </Box>

          {myWaitlist.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd", display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
                <NotificationsNone sx={{ fontSize: 30, color: darkMode ? "#90caf9" : "#1976d2" }} />
              </Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                대기 중인 레슨이 없어요
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, mx: "auto", lineHeight: 1.7 }}>
                예약이 꽉 찬 시간을 눌러 대기 알림을 신청하면, 자리가 나는 즉시 알려드려요.
              </Typography>
              <Button
                variant="contained"
                startIcon={<EventAvailable />}
                onClick={() => setTabValue(0)}
                sx={{ mt: 2.5, textTransform: "none", borderRadius: 2 }}
              >
                레슨 신청하러 가기
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 2 }}>
              {myWaitlist.map(w => {
                const dday = getWaitlistDday(w.date);
                const isPast = dday.tone === "past";
                const tile = {
                  today: { bg: darkMode ? "rgba(244, 67, 54, 0.18)" : "#ffebee", week: darkMode ? "#e57373" : "#d32f2f", day: darkMode ? "#ef9a9a" : "#c62828" },
                  tomorrow: { bg: darkMode ? "rgba(255, 152, 0, 0.18)" : "#fff3e0", week: darkMode ? "#ffa726" : "#e65100", day: darkMode ? "#ffb74d" : "#e65100" },
                  upcoming: { bg: darkMode ? "rgba(144, 202, 249, 0.16)" : "#e3f2fd", week: darkMode ? "#64b5f6" : "#1976d2", day: darkMode ? "#90caf9" : "#1565c0" },
                  past: { bg: darkMode ? "rgba(255, 255, 255, 0.06)" : "#f0f0f0", week: darkMode ? "#8a8a8a" : "#999", day: darkMode ? "#9e9e9e" : "#888" },
                }[dday.tone];
                const chip = {
                  today: { bg: darkMode ? "rgba(244, 67, 54, 0.2)" : "#ffebee", color: darkMode ? "#ef9a9a" : "#d32f2f" },
                  tomorrow: { bg: darkMode ? "rgba(255, 152, 0, 0.2)" : "#fff3e0", color: darkMode ? "#ffb74d" : "#e65100" },
                  upcoming: { bg: darkMode ? "rgba(255, 255, 255, 0.08)" : "#eef2f7", color: darkMode ? "#b0b8c4" : "#546074" },
                  past: { bg: darkMode ? "rgba(255, 255, 255, 0.08)" : "#eceff1", color: "#90a4ae" },
                }[dday.tone];
                const created = w.created_at ?? w.createdAt;

                return (
                  <Box
                    key={w.id}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: darkMode ? "rgba(255, 255, 255, 0.03)" : "#fff",
                      border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e8e8e8",
                      opacity: isPast ? 0.6 : 1,
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ flex: "none", width: 52, borderRadius: 1.5, bgcolor: tile.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 0.75 }}>
                      <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: tile.week, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                        {dayjs(w.date).month() + 1}월
                      </Typography>
                      <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: tile.day, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                        {dayjs(w.date).date()}일
                      </Typography>
                      <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: tile.week, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                        ({["일", "월", "화", "수", "목", "금", "토"][dayjs(w.date).day()]})
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: darkMode ? "#e8e8e8" : "#222", whiteSpace: "nowrap" }}>
                          {w.start_time.slice(0, 5)} ~ {getEndTime(w.start_time)}
                        </Typography>
                        <Chip
                          label={dday.label}
                          size="small"
                          sx={{ flex: "none", height: 22, fontSize: "0.7rem", fontWeight: 700, bgcolor: chip.bg, color: chip.color, "& .MuiChip-label": { px: 1 } }}
                        />
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary", minWidth: 0 }}>
                        {w.teacher
                          ? <Person sx={{ fontSize: "1rem", flex: "none" }} />
                          : <Group sx={{ fontSize: "1rem", flex: "none" }} />}
                        <Typography variant="body2" noWrap>
                          {w.teacher ? `${w.teacher.name} 선생님` : "선생님 지정 안 함"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled" }}>
                          {created && (
                            <>
                              <AccessTime sx={{ fontSize: "0.85rem", flex: "none" }} />
                              <Typography variant="caption" noWrap>{getRelativeCreated(created)}</Typography>
                            </>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Close sx={{ fontSize: "1rem" }} />}
                          onClick={() => handleCancelWaitlist(w.id)}
                          sx={{ flex: "none", textTransform: "none", borderRadius: 1.5, py: 0.4, px: 1.2, fontSize: "0.8rem", minWidth: 0, whiteSpace: "nowrap", "& .MuiButton-startIcon": { mr: 0.5 } }}
                        >
                          대기 취소
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>레슨 신청</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                선생님
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {confirmDialog.teacher?.name} 선생님
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                날짜
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {confirmDialog.date} ({["일", "월", "화", "수", "목", "금", "토"][dayjs(confirmDialog.date).day()]}요일)
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                시간
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {confirmDialog.time} ~ {getEndTime(confirmDialog.time)}
              </Typography>
            </Box>

            {getPendingCount(confirmDialog.time, confirmDialog.teacher?.id) > 0 && (
              <Box sx={{ p: 2, bgcolor: darkMode ? "rgba(255, 235, 238, 0.2)" : "#ffebee", borderRadius: 1, border: "1px solid #f44336" }}>
                <Typography variant="body2" fontWeight="600" color="#d32f2f">
                  ⚠️ 현재 이 시간에 {getPendingCount(confirmDialog.time, confirmDialog.teacher?.id)}명이 신청 대기 중입니다.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  먼저 신청한 회원이 승인될 수 있습니다.
                </Typography>
              </Box>
            )}

            {/* 변경권 티켓 선택 UI */}
            {userRescheduleTicketCount > 0 && (userTicket - userRescheduleTicketCount) > 0 && (
              <Box sx={{ p: 2, bgcolor: darkMode ? "rgba(156,39,176,0.12)" : "#f3e5f5", borderRadius: 1, border: "1px solid #9c27b0" }}>
                <Typography variant="body2" fontWeight="700" color="#7b1fa2" sx={{ mb: 1.5 }}>
                  🎫 사용할 티켓 선택
                </Typography>
                <Stack spacing={1}>
                  <Box
                    onClick={() => setUseRescheduleTicket(false)}
                    sx={{
                      p: 1.5, borderRadius: 1, cursor: 'pointer',
                      border: '2px solid',
                      borderColor: !useRescheduleTicket ? '#1976d2' : 'divider',
                      bgcolor: !useRescheduleTicket ? (darkMode ? 'rgba(25,118,210,0.15)' : '#e3f2fd') : 'transparent',
                    }}
                  >
                    <Typography variant="body2" fontWeight="600">
                      일반 티켓 ({userTicket - userRescheduleTicketCount}장)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      이 티켓으로 레슨 예약 후 1회 취소 신청이 가능합니다.
                    </Typography>
                  </Box>
                  <Box
                    onClick={() => setUseRescheduleTicket(true)}
                    sx={{
                      p: 1.5, borderRadius: 1, cursor: 'pointer',
                      border: '2px solid',
                      borderColor: useRescheduleTicket ? '#d32f2f' : 'divider',
                      bgcolor: useRescheduleTicket ? (darkMode ? 'rgba(211,47,47,0.12)' : '#ffebee') : 'transparent',
                    }}
                  >
                    <Typography variant="body2" fontWeight="600">
                      변경 티켓 ({userRescheduleTicketCount}장)
                    </Typography>
                    <Typography variant="caption" color="error">
                      ⚠️ 이 티켓으로 예약한 레슨은 추가 취소 신청이 불가합니다.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {/* 변경권 티켓만 보유 시 경고 */}
            {userRescheduleTicketCount > 0 && (userTicket - userRescheduleTicketCount) <= 0 && (
              <Box sx={{ p: 2, bgcolor: darkMode ? "rgba(211,47,47,0.15)" : "#ffebee", borderRadius: 1, border: "2px solid #d32f2f" }}>
                <Typography variant="body2" fontWeight="700" color="error" sx={{ mb: 0.5 }}>
                  ⚠️ 변경권 티켓 사용 안내
                </Typography>
                <Typography variant="body2" color="error">
                  현재 보유하신 티켓은 이전 레슨 취소로 발급된 <strong>변경권 티켓</strong>입니다.<br />
                  이 티켓으로 예약한 레슨은 <strong>추가로 취소 신청할 수 없습니다.</strong><br />
                  취소가 필요한 경우 반드시 관리자에게 문의해주세요.
                </Typography>
              </Box>
            )}

            <Box sx={{ p: 2, bgcolor: darkMode ? "rgba(255, 243, 224, 0.15)" : "#fff3e0", borderRadius: 1, border: "1px solid #ff9800" }}>
              <Typography variant="body2" fontWeight="600" color="#ed6c02" sx={{ mb: 1 }}>
                ⚠️ 신청 시 주의사항
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 신청 시 티켓 1개가 즉시 차감됩니다<br />
                • 관리자 승인 시 예약 확정됩니다<br />
                • 관리자 반려 시 티켓이 복구됩니다
                {useRescheduleTicket && (
                  <><br /><strong style={{ color: '#d32f2f' }}>• 변경권 티켓 사용 시 확정 후 취소 신청 불가</strong></>
                )}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialog({ open: false })} disabled={loading}>
            취소
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "신청"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={waitlistDialog.open} onClose={() => setWaitlistDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>예약 대기 알림</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {waitlistDialog.teacher_name && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  선생님
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {waitlistDialog.teacher_name} 선생님
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                날짜
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {waitlistDialog.date} ({["일", "월", "화", "수", "목", "금", "토"][dayjs(waitlistDialog.date).day()]}요일)
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                시간
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {waitlistDialog.time} ~ {getEndTime(waitlistDialog.time)}
              </Typography>
            </Box>

            <Box sx={{ p: 2, bgcolor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd", borderRadius: 1, border: "1px solid #1976d2" }}>
              <Typography variant="body2" fontWeight="600" color="#1976d2" sx={{ mb: 1 }}>
                📢 대기 알림 안내
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 현재 이 시간은 예약이 마감되었습니다<br />
                • 대기 알림을 신청하면 다른 회원이 취소할 경우 푸시 알림을 받습니다<br />
                • 알림을 받으면 빠르게 예약하세요
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setWaitlistDialog({ open: false })} disabled={loading}>
            취소
          </Button>
          <Button
            onClick={handleWaitlistConfirm}
            variant="contained"
            disabled={loading}
            startIcon={<NotificationsActive />}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "대기 알림 신청"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PullToRefresh>
  );
}

export default UserLessonPage;